import { useEffect } from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Sparkles } from "lucide-react";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { fadeUp, stagger } from "@/lib/animations";
import { difficultyLabel, formatDuration, type GuideDetail } from "@/lib/guides";
import Footer from "@/components/ui/Footer";
import Blob from "@/components/ui/Blob";
import WaveDivider from "@/components/ui/WaveDivider";
import EmailSignupForm from "@/components/ui/EmailSignupForm";
import VideoEmbed from "@/components/guides/VideoEmbed";
import ProductsUsedCard from "@/components/guides/ProductsUsedCard";
import MarkdownBody from "@/components/guides/MarkdownBody";
import LikeButton from "@/components/guides/LikeButton";
import MetaPill from "@/components/guides/MetaPill";
import RelatedGuides from "@/components/guides/RelatedGuides";

type Props = {
  guide: GuideDetail;
};

export default function GuideDetailPage({ guide }: Props) {
  const theme = usePalette();
  const duration = formatDuration(guide.durationMins);
  const difficulty = difficultyLabel(guide.difficulty);
  const topicAccent = guide.topic?.color ?? theme.accent;

  useEffect(() => {
    document.title = `${guide.seoTitle ?? guide.title} · A Few Loose Threads`;
    if (guide.seoDescription) {
      const meta =
        document.querySelector('meta[name="description"]') ?? document.createElement("meta");
      meta.setAttribute("name", "description");
      meta.setAttribute("content", guide.seoDescription);
      if (!meta.parentNode) document.head.appendChild(meta);
    }
  }, [guide.title, guide.seoTitle, guide.seoDescription]);

  return (
    <div className="min-h-dvh" style={{ backgroundColor: colors.cream }}>

      {/* Hero */}
      <section
        className="relative overflow-hidden pt-32 pb-12"
        style={{ backgroundColor: colors.cream }}
      >
        <Blob className="w-96 h-96 -top-20 -left-10" color={theme.blob1} />
        <Blob className="w-72 h-72 top-20 right-0" color={theme.blob3} />

        <div className="relative z-10 mx-auto max-w-4xl px-6">
          <Link
            to={guide.topic ? `/guides/${guide.topic.slug}` : "/guides"}
            className="mb-6 inline-flex items-center gap-2 text-xs uppercase"
            style={{
              fontFamily: fonts.body,
              fontWeight: 700,
              letterSpacing: "0.16em",
              color: `${colors.navy}88`,
            }}
          >
            <ArrowLeft size={14} />
            {guide.topic ? guide.topic.name : "All guides"}
          </Link>

          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.div
              variants={fadeUp}
              custom={0}
              className="mb-4 flex flex-wrap items-center gap-2"
            >
              {guide.topic && (
                <MetaPill tone="accent" accentColor={topicAccent}>
                  <Sparkles size={12} />
                  {guide.topic.name}
                </MetaPill>
              )}
              {duration && (
                <MetaPill>
                  <Clock size={12} />
                  {duration}
                </MetaPill>
              )}
              {difficulty && <MetaPill tone="warm">{difficulty}</MetaPill>}
            </motion.div>

            <motion.h1
              variants={fadeUp}
              custom={1}
              className="text-5xl sm:text-6xl"
              style={{
                fontFamily: fonts.logo,
                fontWeight: 400,
                color: colors.navy,
                lineHeight: 1.08,
              }}
            >
              {guide.title}
            </motion.h1>
            {guide.subtitle && (
              <motion.p
                variants={fadeUp}
                custom={2}
                className="mt-5 max-w-2xl text-lg"
                style={{
                  fontFamily: fonts.body,
                  fontWeight: 500,
                  color: `${colors.navy}b3`,
                  letterSpacing: "0.02em",
                  lineHeight: 1.6,
                }}
              >
                {guide.subtitle}
              </motion.p>
            )}

            <motion.div variants={fadeUp} custom={3} className="mt-6">
              <LikeButton
                guideSlug={guide.slug}
                initialCount={guide.likeCount}
                initialLiked={guide.likedByViewer}
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Video — centred above the body/sidebar grid */}
      <section className="pb-12" style={{ backgroundColor: colors.cream }}>
        <div className="mx-auto max-w-4xl px-6">
          <VideoEmbed
            youtubeId={guide.videoYoutubeId}
            instagramUrl={guide.videoInstagramUrl}
            title={guide.title}
            posterUrl={guide.heroImageUrl}
          />
        </div>
      </section>

      <WaveDivider color={theme.sectionBg2} />

      {/* Body — centred on the viewport with products as a true sidebar.
          Mobile: products card stacks above the body so picks stay near the video.
          Desktop: 4-column grid puts a 720px prose column dead-centre with a 320px
          products column attached to its right within the page's gutter. */}
      <section className="py-16" style={{ backgroundColor: theme.sectionBg2 }}>
        <div className="px-6 lg:grid lg:grid-cols-[1fr_minmax(0,720px)_320px_1fr] lg:gap-x-10">
          <aside className="mx-auto mb-12 max-w-2xl lg:col-start-3 lg:row-start-1 lg:mb-0 lg:max-w-none">
            <ProductsUsedCard products={guide.products} sticky />
          </aside>

          <div className="mx-auto max-w-2xl lg:col-start-2 lg:row-start-1 lg:max-w-none">
            <MarkdownBody>{guide.bodyMarkdown}</MarkdownBody>

            {guide.personalNote && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                className="mt-12 rounded-3xl p-7"
                style={{
                  backgroundColor: colors.cream,
                  border: `1px solid ${colors.tan}33`,
                }}
              >
                <p
                  className="mb-2 text-[11px] uppercase"
                  style={{
                    fontFamily: fonts.body,
                    fontWeight: 800,
                    letterSpacing: "0.18em",
                    color: theme.accent,
                  }}
                >
                  A note from Chelsea
                </p>
                <p
                  style={{
                    fontFamily: fonts.heading,
                    fontSize: "1.6rem",
                    color: colors.navy,
                    lineHeight: 1.4,
                  }}
                >
                  {guide.personalNote}
                </p>
              </motion.div>
            )}

            {guide.products.length > 0 && (
              <p
                className="mt-12 text-center text-[11px] leading-relaxed"
                style={{
                  fontFamily: fonts.body,
                  fontWeight: 500,
                  letterSpacing: "0.06em",
                  color: `${colors.navy}77`,
                }}
              >
                Some links in this guide are affiliate links — Chelsea may earn a small
                commission at no extra cost to you. Only products she actually uses or
                would recommend make it in.
              </p>
            )}
          </div>
        </div>
      </section>

      <WaveDivider color={theme.sectionBg} />

      {/* Newsletter CTA */}
      <section className="py-20" style={{ backgroundColor: theme.sectionBg }}>
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p
            className="mb-2 text-xs uppercase"
            style={{
              fontFamily: fonts.body,
              fontWeight: 800,
              letterSpacing: "0.2em",
              color: theme.accent,
            }}
          >
            More like this
          </p>
          <h2
            className="mb-4 text-3xl lg:text-4xl"
            style={{ fontFamily: fonts.logo, color: colors.navy, fontWeight: 400 }}
          >
            Want the next{" "}
            <span style={{ fontFamily: fonts.heading, color: theme.warm }}>
              {guide.topic?.name.toLowerCase() ?? "guide"}
            </span>{" "}
            sent to you?
          </h2>
          <p
            className="mx-auto mb-8 max-w-md text-sm"
            style={{
              fontFamily: fonts.body,
              fontWeight: 500,
              color: `${colors.navy}99`,
              letterSpacing: "0.03em",
              lineHeight: 1.7,
            }}
          >
            One short email when a new guide drops. No spam — promise.
          </p>
          <div className="mx-auto max-w-md">
            <EmailSignupForm
              source={`guide-${guide.topic?.slug ?? "all"}-${guide.slug}`}
              compact
            />
          </div>
        </div>
      </section>

      <RelatedGuides related={guide.related} topicName={guide.topic?.name} />

      <Footer />
    </div>
  );
}
